import { apiClient } from './client'
import { cache, getCacheKey } from '../cache'
import type { Brandshop } from './brandshops'

export const HISTORY_SPEC_VERSION = "2026-08-05-spec-v11-ddr-fact"
type ShopKind = 'regular' | 'bigshop' | 'brandshop'

export async function resolveShopKindById(shopId: string): Promise<ShopKind | null> {
  const cacheKey = getCacheKey('shops', 'list')
  const cached = cache.get<Shop[]>(cacheKey)
  const fromCache = cached?.find((s: Shop) => s.id === shopId)?.shop_kind
  if (fromCache) return fromCache
  try {
    const { data } = await apiClient.get<Shop[]>('/api/v1/users/me/shops')
    cache.set(cacheKey, data, 30 * 60 * 1000)
    return data.find((s: Shop) => s.id === shopId)?.shop_kind || null
  } catch {
    return null
  }
}

export async function resolveShopSidById(shopId: string): Promise<string | null> {
  const cacheKey = getCacheKey('shops', 'list')
  const cached = cache.get<Shop[]>(cacheKey)
  const fromCache = cached?.find((s: Shop) => s.id === shopId)?.sid
  if (fromCache) return fromCache
  try {
    const { data } = await apiClient.get<Shop[]>('/api/v1/users/me/shops')
    cache.set(cacheKey, data, 30 * 60 * 1000)
    return data.find((s: Shop) => s.id === shopId)?.sid || null
  } catch {
    return null
  }
}

export interface ShopSubscription {
  tariff_id?: string | null
  status?: string | null // active, canceled, expired
  auto_renew?: boolean | null
  payment_method_id?: string | null
  yookassa_subscription_id?: string | null
  current_period_start?: string | null
  current_period_end?: string | null
  trial_used?: boolean | null
}

export interface Shop {
  id: string
  sid: string | null
  name: string
  shop_kind?: 'regular' | 'bigshop' | 'brandshop'
  wb_api_key?: string | null
  rnp_url?: string | null
  description?: string
  date_added?: string | null
  updated_at?: string | null
  subscription?: ShopSubscription | null
  trial_used?: boolean | null
  created_at?: string
  role?: string | null
}

export interface ArticleMetric {
  title: string
  key: string
  period: string | null
  unit: string
  value: string // Форматированное значение (строка)
}

export interface ArticleGroup {
  color: string // Цвет группы в формате HSL
  metrics: ArticleMetric[]
}

export interface ArticleHistoryRecord {
  article: string // ID артикула
  target_date: string // ISO datetime string
  updateAt: string // ISO datetime string (deprecated, используйте updated_at)
  updated_at?: string // ISO datetime string - когда обновилась запись за эту дату
  groups: Record<string, ArticleGroup> // Группы метрик: {"Основные параметры": {"color": "...", "metrics": [...]}, ...}
  meta: Record<string, ArticleGroup> | null // Meta метрики: {"По кабинету": {"color": "...", "metrics": [...]}}
}

export interface ArticleHistoryResponse {
  items: ArticleHistoryRecord[]
  per_page: number
  next_cursor: string | null
}

export interface ArticleListItem {
  nmID: number
  title: string
  photos?: string | null
  isFavorite?: boolean
  vendorCode?: string | null
}

export interface ArticleListResponse {
  items: ArticleListItem[]
  total: number
}

export interface ArticleRating {
  rating: number
  count: string
}

export interface ArticleStatistic {
  nmID: number
  status: 'week' | 'three' | 'none'
  // Статистика за неделю (7 дней)
  weekStat_marjaDay_7d?: number | null
  weekStat_marjaDay_14d?: number | null
  weekStat_marjaDay_30d?: number | null
  weekStat_marjaDay_90d?: number | null
  weekStat_marjaDay_custom?: number | null
  weekStat_calcDDR_7d?: number | null
  weekStat_calcDDR_14d?: number | null
  weekStat_calcDDR_30d?: number | null
  weekStat_calcDDR_90d?: number | null
  weekStat_calcDDR_custom?: number | null
  weekStat_adSum?: number | null
  // Статистика за 3 дня
  threeStat_marjaDay_7d?: number | null
  threeStat_marjaDay_14d?: number | null
  threeStat_marjaDay_30d?: number | null
  threeStat_marjaDay_90d?: number | null
  threeStat_marjaDay_custom?: number | null
  threeStat_calcDDR_7d?: number | null
  threeStat_calcDDR_14d?: number | null
  threeStat_calcDDR_30d?: number | null
  threeStat_calcDDR_90d?: number | null
  threeStat_calcDDR_custom?: number | null
  threeStat_adSum?: number | null
}

export interface ArticleDetailResponse {
  nmID: number
  subjectName?: string | null
  vendorCode?: string | null
  brand?: string | null
  title?: string | null
  photos?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  link: string
  rating?: ArticleRating | null
  is_favorite: boolean
  cost_price?: number | null
  tags: string[]
  statistic?: ArticleStatistic | null
}

export interface ArticlesStatisticRequest {
  article_ids: number[]
}

export interface ArticlesStatisticResponse {
  items: ArticleStatistic[]
}

export interface ArticleSearchRequest {
  query?: string | null
  subjectName?: string | null
  vendorCode?: string | null
  brand?: string | null
  title?: string | null
  nmID?: number | null
  page?: number
  per_page?: number
}

export interface ArticleSearchResponse {
  items: ArticleListItem[]
  total: number
  page: number
  per_page: number
}

// Управление артикулами
export interface ArticleManageItem {
  nmID: number
  title: string
  photos?: string | null
  category?: string | null
  supplierArticle?: string | null
  cost_price?: number | null
  buyout_percent?: number | null
  tag?: string | null
}

export interface ArticleManageResponse {
  items: ArticleManageItem[]
  total: number
  page: number
  per_page: number
}

export interface ArticleManageUpdateRequest {
  cost_price?: number | null
  tag?: string | null
  buyout_percent?: number | null
}

export interface ArticleManageUpdateResponse {
  nmID: number
  cost_price?: number | null
  tag?: string | null
  buyout_percent?: number | null
}

export interface HistoryMetricSpec {
  title: string
  key: string
  description: string
  unit: string
  requires_period: boolean
  sign_display: string
}

export interface HistoryMetricGroupSpec {
  title: string
  color: string
  metrics: HistoryMetricSpec[]
}

export interface HistoryMetricsSpecResponse {
  groups: HistoryMetricGroupSpec[]
}

export type HistoryDayDataResponse = Record<string, any>

export interface HistoryDayItem {
  target_date: string
  article: Record<string, any>
  cabinet: Record<string, any>
  updated_at?: string // ISO datetime string - когда обновилась запись за эту дату
}

export interface HistoryResponse {
  items: HistoryDayItem[]
}

// Интерфейсы для новой логики множественных артикулов
export interface ArticleHistoryDayItem {
  [key: string]: any
  target_date: string
  updated_at?: string | null
  ordersCount?: number | null
  ordersSum?: number | null
  finishedPrice?: number | null
  buyoutPercent_7d?: number | null
  buyoutPercent_14d?: number | null
  buyoutPercent_30d?: number | null
  buyoutPercent_90d?: number | null
  buyoutPercent_custom?: number | null
  buyoutCount_7d?: number | null
  buyoutCount_14d?: number | null
  buyoutCount_30d?: number | null
  buyoutCount_90d?: number | null
  buyoutCount_custom?: number | null
  expectedRevenue_7d?: number | null
  expectedRevenue_14d?: number | null
  expectedRevenue_30d?: number | null
  expectedRevenue_90d?: number | null
  expectedRevenue_custom?: number | null
  adSum?: number | null
  calcDDR_7d?: number | null
  calcDDR_14d?: number | null
  calcDDR_30d?: number | null
  calcDDR_90d?: number | null
  calcDDR_custom?: number | null
  marjaDay_7d?: number | null
  marjaDay_14d?: number | null
  marjaDay_30d?: number | null
  marjaDay_90d?: number | null
  marjaDay_custom?: number | null
  factMarjaDay_7d?: number | null
  factMarjaDay_14d?: number | null
  factMarjaDay_30d?: number | null
  factMarjaDay_90d?: number | null
  factMarjaDay_custom?: number | null
  stockCount?: number | null
}

export interface ArticleHistoryItem {
  nmID: number
  vendorCode?: string | null
  is_favorite: boolean
  days: ArticleHistoryDayItem[]
}

export interface ArticlesHistoryResponse {
  items: ArticleHistoryItem[]
  total: number
  page: number
  per_page: number
}

export interface ArticleDetailListResponse {
  items: ArticleDetailResponse[]
}

export interface ArticleDayDataItem {
  target_date: string
  updated_at?: string | null
  data: Record<string, any>
}

export interface ArticleDayDataListResponse {
  items: ArticleDayDataItem[]
  meta?: {
    total_articles: number
    selected_articles: number
    selection_type: "all" | "brand" | "tag"
    selection_value?: string | null
  } | null
}

export interface UniqueValuesPageResponse {
  items: string[]
  total: number
  page: number
  per_page: number
  has_more: boolean
}

export interface BulkManageUpdateRequest {
  article_ids: number[]
  cost_price?: number | null
  tag?: string | null
  buyout_percent?: number | null
}

export interface BulkManageUpdateResponse {
  updated_count: number
}

export interface BulkDeleteRequest {
  article_ids: number[]
}

export interface BulkDeleteResponse {
  deleted_count: number
}

export interface DeleteArticleManageResponse {
  nmID: number
  deleted: boolean
}

export interface ShopCreateRequest {
  name?: string
  wb_api_key?: string
  brandshop_invite_code?: string
}

export interface ShopApiKeyUpdateRequest {
  wb_api_key: string
}

export const shopsApi = {
  // Создать магазин
  createShop: async (request: ShopCreateRequest): Promise<Shop> => {
    const { data } = await apiClient.post<Shop>('/api/v1/shops', request)
    
    // Инвалидируем кэш списка магазинов
    const shopsListCacheKey = getCacheKey('shops', 'list')
    cache.delete(shopsListCacheKey)
    
    // Добавляем созданный магазин в кэш
    const shopCacheKey = getCacheKey('shop', data.id)
    cache.set(shopCacheKey, data, 30 * 60 * 1000)
    
    return data
  },

  // Получить список магазинов пользователя
  listMyShops: async (): Promise<Shop[]> => {
    const cacheKey = getCacheKey('shops', 'list')
    const cached = cache.get<Shop[]>(cacheKey)
    if (cached) {
      return cached
    }

    try {
      const { data } = await apiClient.get<Shop[]>('/api/v1/users/me/shops')
      // Кэшируем на 30 минут
      cache.set(cacheKey, data, 30 * 60 * 1000)
      return data
    } catch (error: any) {
      // Для редких кратковременных 5xx возвращаем безопасный пустой список,
      // чтобы не ронять критичные экраны (settings/payment).
      if (error?.response?.status >= 500) {
        return []
      }
      throw error
    }
  },

  // Получить детальную информацию об артикуле
  getArticleDetail: async (
    shopId: string,
    articleId: string
  ): Promise<ArticleDetailResponse> => {
    const { data } = await apiClient.get<ArticleDetailResponse>(
      `/api/v1/shops/${shopId}/articles/${articleId}/detail`
    )
    return data
  },

  // Получить детальную информацию о множестве артикулов (bulk запрос)
  getArticlesDetailBulk: async (
    shopId: string,
    articleIds: number[]
  ): Promise<ArticleDetailListResponse> => {
    if (articleIds.length === 0) {
      return { items: [] }
    }
    
    // Ограничиваем до 100 артикулов за запрос
    const idsToFetch = articleIds.slice(0, 100)
    
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const detailRequests = idsToFetch.map(async (articleId) => {
        try {
          const { data } = await apiClient.get<ArticleDetailResponse>(
            `/api/v1/brandshops/${shopId}/articles/${articleId}/detail`
          )
          return data
        } catch {
          return null
        }
      })
      const details = await Promise.all(detailRequests)
      return {
        items: details.filter(
          (item: ArticleDetailResponse | null): item is ArticleDetailResponse => item !== null
        ),
      }
    }

    const { data } = await apiClient.post<ArticleDetailListResponse>(
      `/api/v1/shops/${shopId}/articles/detail/bulk`,
      { article_ids: idsToFetch }
    )
    return data
  },

  // Получить статистику процентных изменений для множества артикулов (bulk запрос)
  getArticlesStatistic: async (
    shopId: string,
    articleIds: number[]
  ): Promise<ArticlesStatisticResponse> => {
    if (articleIds.length === 0) {
      return { items: [] }
    }
    
    // Ограничиваем до 100 артикулов за запрос
    const idsToFetch = articleIds.slice(0, 100)
    
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.post<ArticlesStatisticResponse>(
        `/api/v1/brandshops/${shopId}/articles/statistic`,
        { article_ids: idsToFetch }
      )
      return data
    }

    const { data } = await apiClient.post<ArticlesStatisticResponse>(
      `/api/v1/shops/${shopId}/articles/statistic`,
      { article_ids: idsToFetch }
    )
    return data
  },

  // Поиск артикулов
  searchArticles: async (
    shopId: string,
    searchRequest: ArticleSearchRequest
  ): Promise<ArticleSearchResponse> => {
    const { data } = await apiClient.post<ArticleSearchResponse>(
      `/api/v1/shops/${shopId}/articles/search`,
      searchRequest
    )
    return data
  },


  // Добавить/удалить артикул из избранного
  setFavorite: async (
    shopId: string,
    articleId: string,
    nmID: number,
    isFavorite: boolean
  ): Promise<{ nmID: number; is_favorite: boolean }> => {
    const { data } = await apiClient.post<{ nmID: number; is_favorite: boolean }>(
      `/api/v1/shops/${shopId}/articles/${articleId}/favorite`,
      { nmID, is_favorite: isFavorite }
    )
    return data
  },

  // Получить магазин
  getShop: async (shopId: string): Promise<Shop> => {
    const cacheKey = getCacheKey('shop', shopId)
    const cached = cache.get<Shop>(cacheKey)
    if (cached) {
      return cached
    }

    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.get<Brandshop>(`/api/v1/brandshops/${shopId}`)
      const mappedShop: Shop = {
        id: data.id,
        sid: data.sid ?? null,
        name: data.name,
        shop_kind: 'brandshop',
        wb_api_key: null,
        rnp_url: null,
        subscription: data.subscription
          ? {
              tariff_id: data.subscription.tariff_id ?? null,
              status: data.subscription.status ?? null,
              auto_renew: data.subscription.auto_renew ?? null,
              current_period_start: data.subscription.current_period_start ?? null,
              current_period_end: data.subscription.current_period_end ?? null,
            }
          : null,
        trial_used: data.trial_used ?? null,
        date_added: data.created_at ?? null,
        updated_at: data.updated_at ?? null,
      }

      cache.set(cacheKey, mappedShop, 30 * 60 * 1000)
      return mappedShop
    }

    const { data } = await apiClient.get<Shop>(`/api/v1/shops/${shopId}`)
    // Кэшируем на 30 минут
    cache.set(cacheKey, data, 30 * 60 * 1000)
    return data
  },

  // Активировать пробную версию
  activateTrial: async (shopId: string): Promise<Shop> => {
    const { data } = await apiClient.post<Shop>(`/api/v1/shops/${shopId}/activate-trial`)
    
    // Инвалидируем кэш магазина и списка магазинов
    const shopCacheKey = getCacheKey('shop', shopId)
    cache.delete(shopCacheKey)
    const shopsListCacheKey = getCacheKey('shops', 'list')
    cache.delete(shopsListCacheKey)
    
    // Обновляем кэш магазина новыми данными
    cache.set(shopCacheKey, data, 30 * 60 * 1000)
    
    return data
  },

  // Обновить WB API ключ (только владелец)
  updateWbApiKey: async (shopId: string, request: ShopApiKeyUpdateRequest): Promise<Shop> => {
    const { data } = await apiClient.put<Shop>(`/api/v1/shops/${shopId}/wb-api-key`, request)

    const shopCacheKey = getCacheKey('shop', shopId)
    cache.delete(shopCacheKey)
    const shopsListCacheKey = getCacheKey('shops', 'list')
    cache.delete(shopsListCacheKey)
    cache.set(shopCacheKey, data, 30 * 60 * 1000)

    return data
  },

  // Управление артикулами
  getArticlesManage: async (
    shopId: string,
    params: {
      page?: number
      per_page?: number
      sort_by?: string
      sort_order?: 'asc' | 'desc'
      include_deleted?: boolean
      tag?: string
      category?: string
      brand?: string
      vendorCode?: string
      cost_price_min?: number
      cost_price_max?: number
      search?: string
    }
  ): Promise<ArticleManageResponse> => {
    // Убираем undefined, null и пустые строки из параметров
    // Не передаем include_deleted если оно false (значение по умолчанию)
    const cleanParams: Record<string, any> = {}
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Пропускаем include_deleted=false, так как это значение по умолчанию
        if (key === 'include_deleted' && value === false) {
          return
        }
        cleanParams[key] = value
      }
    })
    
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.get<ArticleManageResponse>(
        `/api/v1/brandshops/${shopId}/articles/manage`,
        { params: cleanParams }
      )
      return data
    }

    const { data } = await apiClient.get<ArticleManageResponse>(
      `/api/v1/shops/${shopId}/articles/manage`,
      { params: cleanParams }
    )
    return data
  },

  updateArticleManage: async (
    shopId: string,
    articleId: string,
    request: ArticleManageUpdateRequest
  ): Promise<ArticleManageUpdateResponse> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.put<ArticleManageUpdateResponse>(
        `/api/v1/brandshops/${shopId}/articles/manage/${articleId}`,
        request
      )
      return data
    }

    const { data } = await apiClient.put<ArticleManageUpdateResponse>(
      `/api/v1/shops/${shopId}/articles/manage/${articleId}`,
      request
    )
    return data
  },

  bulkUpdateArticlesManage: async (
    shopId: string,
    request: BulkManageUpdateRequest
  ): Promise<BulkManageUpdateResponse> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      throw new Error('Для брендшопа массовое обновление недоступно')
    }

    const { data } = await apiClient.put<BulkManageUpdateResponse>(
      `/api/v1/shops/${shopId}/articles/manage/bulk-update`,
      request
    )
    return data
  },

  deleteArticleManage: async (
    shopId: string,
    articleId: string
  ): Promise<DeleteArticleManageResponse> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      // Только артикулы брендов этого brandshop; чужие на бэкенде отсекаются.
      const { data } = await apiClient.delete<DeleteArticleManageResponse>(
        `/api/v1/brandshops/${shopId}/articles/manage/${articleId}`
      )
      return data
    }

    const { data } = await apiClient.delete<DeleteArticleManageResponse>(
      `/api/v1/shops/${shopId}/articles/manage/${articleId}`
    )
    return data
  },

  bulkDeleteArticlesManage: async (
    shopId: string,
    request: BulkDeleteRequest
  ): Promise<BulkDeleteResponse> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      // Только артикулы брендов этого brandshop; чужие nmID игнорируются.
      const { data } = await apiClient.post<BulkDeleteResponse>(
        `/api/v1/brandshops/${shopId}/articles/manage/bulk-delete`,
        request
      )
      return data
    }

    const { data } = await apiClient.post<BulkDeleteResponse>(
      `/api/v1/shops/${shopId}/articles/manage/bulk-delete`,
      request
    )
    return data
  },

  // Получить спецификацию метрик истории
  getHistoryMetricsSpec: async (): Promise<HistoryMetricsSpecResponse> => {
    const { data } = await apiClient.get<HistoryMetricsSpecResponse>(
      `/api/v1/metrics/history/spec`,
      {
        params: {
          v: HISTORY_SPEC_VERSION,
        },
      }
    )
    return data
  },

  // Получить историю всех артикулов магазина за последние N дней
  getAllArticlesHistory: async (
    shopId: string,
    days: number,
    params: {
      page?: number
      per_page?: number
      sort_by?: string
      sort_order?: "asc" | "desc"
      category?: string
      brand?: string
      vendorCode?: string
      tag?: string
      query?: string
      full?: boolean
    }
  ): Promise<ArticlesHistoryResponse> => {
    const queryParams: Record<string, any> = {}
    if (params.page !== undefined) queryParams.page = params.page
    if (params.per_page !== undefined) queryParams.per_page = params.per_page
    if (params.sort_by) queryParams.sort_by = params.sort_by
    if (params.sort_order) queryParams.sort_order = params.sort_order
    if (params.category) queryParams.category = params.category
    if (params.brand) queryParams.brand = params.brand
    if (params.vendorCode) queryParams.vendorCode = params.vendorCode
    if (params.tag) queryParams.tag = params.tag
    if (params.query) queryParams.query = params.query
    if (params.full !== undefined) queryParams.full = params.full

    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      try {
        const { data } = await apiClient.get<ArticlesHistoryResponse>(
          `/api/v1/brandshops/${shopId}/articles/history/${days}`,
          { params: queryParams }
        )
        return data
      } catch (error: any) {
        const detail = error?.response?.data?.detail
        const code = error?.response?.data?.code
        const isForbidden = error?.response?.status === 403
        const isSubscriptionInactive =
          isForbidden &&
          (code === 'brandshop_subscription_inactive' ||
            (typeof detail === 'string' && detail.toLowerCase().includes('подписка brandshop неактивна')))
        if (isSubscriptionInactive) {
          return {
            items: [],
            total: 0,
            page: params.page ?? 1,
            per_page: params.per_page ?? 15,
          }
        }
        throw error
      }
    }

    try {
      const { data } = await apiClient.get<ArticlesHistoryResponse>(
        `/api/v1/shops/${shopId}/articles/history/${days}`,
        { params: queryParams }
      )
      return data
    } catch (error: any) {
      if (error?.response?.status === 404) {
        // Для brandshop пока нет отдельного history endpoint.
        return {
          items: [],
          total: 0,
          page: params.page ?? 1,
          per_page: params.per_page ?? 15,
        }
      }
      throw error
    }
  },

  // Получить историю одного артикула за последние N дней с данными кабинета
  getArticleHistory: async (
    shopId: string,
    articleId: string,
    days: number
  ): Promise<HistoryResponse> => {
    const { data } = await apiClient.get<HistoryResponse>(
      `/api/v1/shops/${shopId}/articles/${articleId}/${days}`
    )
    return data
  },

  // Получить кабинетные данные для магазина за последние N дней
  getCabinetHistory: async (
    shopId: string,
    days: number,
    brand?: string | null,
    tag?: string | null
  ): Promise<ArticleDayDataListResponse> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const params: Record<string, string> = {}
      if (brand && brand.trim()) {
        params.brand = brand.trim()
      } else if (tag && tag.trim()) {
        params.tag = tag.trim()
      }
      const { data } = await apiClient.get<ArticleDayDataListResponse>(
        `/api/v1/brandshops/${shopId}/cabinet/${days}`,
        { params: Object.keys(params).length ? params : undefined }
      )
      return data
    }

    const params: Record<string, string> = {}
    if (brand && brand.trim()) {
      params.brand = brand.trim()
    } else if (tag && tag.trim()) {
      params.tag = tag.trim()
    }
    const { data } = await apiClient.get<ArticleDayDataListResponse>(
      `/api/v1/shops/${shopId}/cabinet/${days}`,
      { params: Object.keys(params).length ? params : undefined }
    )
    return data
  },

  // Получение уникальных значений для фильтров
  getUniqueSubjectNames: async (
    shopId: string,
    params?: { page?: number; per_page?: number; include_deleted?: boolean }
  ): Promise<UniqueValuesPageResponse> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.get<UniqueValuesPageResponse>(
        `/api/v1/brandshops/${shopId}/articles/manage/unique/subjectName`,
        { params }
      )
      return data
    }
    const { data } = await apiClient.get<UniqueValuesPageResponse>(
      `/api/v1/shops/${shopId}/articles/manage/unique/subjectName`,
      { params }
    )
    return data
  },

  getUniqueBrands: async (
    shopId: string,
    params?: { page?: number; per_page?: number; include_deleted?: boolean }
  ): Promise<UniqueValuesPageResponse> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.get<UniqueValuesPageResponse>(
        `/api/v1/brandshops/${shopId}/articles/manage/unique/brand`,
        { params }
      )
      return data
    }
    const { data } = await apiClient.get<UniqueValuesPageResponse>(
      `/api/v1/shops/${shopId}/articles/manage/unique/brand`,
      { params }
    )
    return data
  },

  getUniqueVendorCodes: async (
    shopId: string,
    params?: { page?: number; per_page?: number; include_deleted?: boolean }
  ): Promise<UniqueValuesPageResponse> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.get<UniqueValuesPageResponse>(
        `/api/v1/brandshops/${shopId}/articles/manage/unique/vendorCode`,
        { params }
      )
      return data
    }
    const { data } = await apiClient.get<UniqueValuesPageResponse>(
      `/api/v1/shops/${shopId}/articles/manage/unique/vendorCode`,
      { params }
    )
    return data
  },

  getUniqueTags: async (
    shopId: string,
    params?: { page?: number; per_page?: number; include_deleted?: boolean }
  ): Promise<UniqueValuesPageResponse> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.get<UniqueValuesPageResponse>(
        `/api/v1/brandshops/${shopId}/articles/manage/unique/tag`,
        { params }
      )
      return data
    }
    const { data } = await apiClient.get<UniqueValuesPageResponse>(
      `/api/v1/shops/${shopId}/articles/manage/unique/tag`,
      { params }
    )
    return data
  },

  // Экспорт артикулов в Excel
  exportArticlesToExcel: async (
    shopId: string,
    includeDeleted: boolean = false
  ): Promise<{ task_id: string; status: string; message: string }> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/articles/export/excel`
      : `/api/v1/shops/${shopId}/articles/export/excel`
    const { data } = await apiClient.post<{ task_id: string; status: string; message: string }>(
      endpoint,
      null,
      { params: { include_deleted: includeDeleted } }
    )
    return data
  },

  // Импорт артикулов из Excel
  importArticlesFromExcel: async (
    shopId: string,
    file: File
  ): Promise<{ task_id: string; status: string; message: string; filename: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/articles/import/excel`
      : `/api/v1/shops/${shopId}/articles/import/excel`
    const { data } = await apiClient.post<{ task_id: string; status: string; message: string; filename: string }>(
      endpoint,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return data
  },

  // Получить статус задачи экспорта
  getExportStatus: async (
    shopId: string,
    taskId: string
  ): Promise<{
    task_id: string
    status: string
    message: string
    progress?: any
    result?: any
    error?: string
  }> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/articles/export/excel/${taskId}/status`
      : `/api/v1/shops/${shopId}/articles/export/excel/${taskId}/status`
    const { data } = await apiClient.get(endpoint)
    return data
  },

  // Получить статус задачи импорта
  getImportStatus: async (
    shopId: string,
    taskId: string
  ): Promise<{
    task_id: string
    status: string
    message: string
    progress?: any
    result?: any
    error?: string
  }> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/articles/import/excel/${taskId}/status`
      : `/api/v1/shops/${shopId}/articles/import/excel/${taskId}/status`
    const { data } = await apiClient.get(endpoint)
    return data
  },

  // Скачать экспортированный файл
  downloadExportedFile: async (shopId: string, taskId: string): Promise<Blob> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/articles/export/excel/${taskId}/download`
      : `/api/v1/shops/${shopId}/articles/export/excel/${taskId}/download`
    const { data } = await apiClient.get(endpoint, { responseType: 'blob' })
    return data
  },

  // ========== Change Log (Журнал изменений) ==========

  // Экспорт журнала изменений в Excel
  exportChangeLogToExcel: async (
    shopId: string
  ): Promise<{ task_id: string; status: string; message: string }> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.post<{ task_id: string; status: string; message: string }>(
        `/api/v1/brandshops/${shopId}/change-log/export/excel`
      )
      return data
    }
    const { data } = await apiClient.post<{ task_id: string; status: string; message: string }>(
      `/api/v1/shops/${shopId}/change-log/export/excel`
    )
    return data
  },

  // Импорт журнала изменений из Excel
  importChangeLogFromExcel: async (
    shopId: string,
    file: File
  ): Promise<{ task_id: string; status: string; message: string; filename: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/change-log/import/excel`
      : `/api/v1/shops/${shopId}/change-log/import/excel`

    const { data } = await apiClient.post<{ task_id: string; status: string; message: string; filename: string }>(
      endpoint,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return data
  },

  // Получить статус задачи экспорта журнала изменений
  getChangeLogExportStatus: async (
    shopId: string,
    taskId: string
  ): Promise<{
    task_id: string
    status: string
    message: string
    progress?: any
    result?: any
    error?: string
  }> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/change-log/export/excel/${taskId}/status`
      : `/api/v1/shops/${shopId}/change-log/export/excel/${taskId}/status`
    const { data } = await apiClient.get(endpoint)
    return data
  },

  // Получить статус задачи импорта журнала изменений
  getChangeLogImportStatus: async (
    shopId: string,
    taskId: string
  ): Promise<{
    task_id: string
    status: string
    message: string
    progress?: any
    result?: any
    error?: string
  }> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/change-log/import/excel/${taskId}/status`
      : `/api/v1/shops/${shopId}/change-log/import/excel/${taskId}/status`
    const { data } = await apiClient.get(endpoint)
    return data
  },

  // Скачать экспортированный файл журнала изменений
  downloadChangeLogExportedFile: async (shopId: string, taskId: string): Promise<Blob> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/change-log/export/excel/${taskId}/download`
      : `/api/v1/shops/${shopId}/change-log/export/excel/${taskId}/download`
    const { data } = await apiClient.get(endpoint, { responseType: 'blob' })
    return data
  },

  // ========== Plan and Fact (План/факт) ==========

  exportPlanAndFactToExcel: async (
    shopId: string
  ): Promise<{ task_id: string; status: string; message: string }> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/plan-and-fact/export/excel`
      : `/api/v1/shops/${shopId}/plan-and-fact/export/excel`
    const { data } = await apiClient.post<{ task_id: string; status: string; message: string }>(endpoint)
    return data
  },

  importPlanAndFactFromExcel: async (
    shopId: string,
    file: File
  ): Promise<{ task_id: string; status: string; message: string; filename: string }> => {
    const formData = new FormData()
    formData.append('file', file)

    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/plan-and-fact/import/excel`
      : `/api/v1/shops/${shopId}/plan-and-fact/import/excel`

    const { data } = await apiClient.post<{ task_id: string; status: string; message: string; filename: string }>(
      endpoint,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return data
  },

  getPlanAndFactExportStatus: async (
    shopId: string,
    taskId: string
  ): Promise<{
    task_id: string
    status: string
    message: string
    progress?: any
    result?: any
    error?: string
  }> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/plan-and-fact/export/excel/${taskId}/status`
      : `/api/v1/shops/${shopId}/plan-and-fact/export/excel/${taskId}/status`
    const { data } = await apiClient.get(endpoint)
    return data
  },

  getPlanAndFactImportStatus: async (
    shopId: string,
    taskId: string
  ): Promise<{
    task_id: string
    status: string
    message: string
    progress?: any
    result?: any
    error?: string
  }> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/plan-and-fact/import/excel/${taskId}/status`
      : `/api/v1/shops/${shopId}/plan-and-fact/import/excel/${taskId}/status`
    const { data } = await apiClient.get(endpoint)
    return data
  },

  downloadPlanAndFactExportedFile: async (shopId: string, taskId: string): Promise<Blob> => {
    const shopKind = await resolveShopKindById(shopId)
    const endpoint = shopKind === 'brandshop'
      ? `/api/v1/brandshops/${shopId}/plan-and-fact/export/excel/${taskId}/download`
      : `/api/v1/shops/${shopId}/plan-and-fact/export/excel/${taskId}/download`
    const { data } = await apiClient.get(endpoint, { responseType: 'blob' })
    return data
  },
}

// ========== Shop About ==========

export interface ShopAbout {
  shop_id: string
  employees_count: number
  subscription_status: string | null
  subscription_end: string | null
}

// ========== Employees ==========

export interface Employee {
  user_id: string | null  // Может быть null, если пользователь еще не зарегистрирован
  email: string | null  // Может быть null
  name: string | null
  telegram_id: number | null
  telegram_username: string | null  // Добавляем новое поле
  role?: 'owner' | 'employee' | string | null
}

export interface EmployeeAddRequest {
  email?: string
  telegram_id?: number
}

export interface ShopInvite {
  id: string
  code: string
  shop_id: string
  grant_role: string
  max_uses?: number | null
  used_count: number
  is_active: boolean
  expires_at?: string | null
  created_by?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export const shopAboutApi = {
  // Получить краткую информацию о магазине
  getAbout: async (shopId: string): Promise<ShopAbout> => {
    const { data } = await apiClient.get<ShopAbout>(`/api/v1/shops/${shopId}/about`)
    return data
  },
}

export const employeesApi = {
  // Получить список сотрудников
  listEmployees: async (shopId: string): Promise<Employee[]> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.get<{
        items: Array<{
          user_id: string | null
          email: string | null
          name: string | null
          telegram_id: number | null
          telegram_username: string | null
          user_identifier?: string | null
          role?: string | null
        }>
      }>(`/api/v1/brandshops/${shopId}/members`)
      return (data.items || []).map((member: {
        user_id: string | null
        email: string | null
        name: string | null
        telegram_id: number | null
        telegram_username: string | null
        user_identifier?: string | null
        role?: string | null
      }) => ({
        user_id: member.user_id ?? null,
        email: member.email ?? null,
        name: member.name ?? null,
        telegram_id: member.telegram_id ?? null,
        telegram_username: member.telegram_username ?? null,
        role: member.role ?? null,
      }))
    }
    const { data } = await apiClient.get<Employee[]>(`/api/v1/shops/${shopId}/employees`)
    return data
  },

  // Добавить сотрудника
  addEmployee: async (shopId: string, request: EmployeeAddRequest): Promise<Employee> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      throw new Error('Для брендшопа добавление сотрудника через email/telegram не поддерживается этим экраном')
    }
    const { data } = await apiClient.post<Employee>(`/api/v1/shops/${shopId}/employees`, request)
    return data
  },

  // Получить invite-код магазина
  createInvite: async (shopId: string): Promise<ShopInvite> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.post<{
        id: string
        code: string
        brandshop_id: string
        grant_role: string
        max_uses?: number | null
        used_count: number
        is_active: boolean
        expires_at?: string | null
        created_by?: string | null
        created_at?: string | null
        updated_at?: string | null
      }>(`/api/v1/brandshops/${shopId}/invites`, { grant_role: 'employee' })
      return {
        id: data.id,
        code: data.code,
        shop_id: data.brandshop_id,
        grant_role: data.grant_role,
        max_uses: data.max_uses ?? null,
        used_count: data.used_count,
        is_active: data.is_active,
        expires_at: data.expires_at ?? null,
        created_by: data.created_by ?? null,
        created_at: data.created_at ?? null,
        updated_at: data.updated_at ?? null,
      }
    }
    const { data } = await apiClient.post<ShopInvite>(`/api/v1/shops/${shopId}/invites`)
    return data
  },

  // Изменить роль сотрудника (admin only)
  updateEmployeeRole: async (
    shopId: string,
    employeeUserId: string,
    role: 'owner' | 'employee'
  ): Promise<Employee> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      const { data } = await apiClient.post<{
        user_id?: string | null
        email?: string | null
        name?: string | null
        telegram_id?: number | null
        telegram_username?: string | null
        role?: string | null
      }>(`/api/v1/brandshops/${shopId}/members`, {
        user_id: employeeUserId,
        role,
      })
      return {
        user_id: data.user_id ?? null,
        email: data.email ?? null,
        name: data.name ?? null,
        telegram_id: data.telegram_id ?? null,
        telegram_username: data.telegram_username ?? null,
        role: data.role ?? null,
      }
    }
    const { data } = await apiClient.patch<Employee>(
      `/api/v1/shops/${shopId}/employees/${employeeUserId}/role`,
      { role }
    )
    return data
  },

  // Удалить сотрудника
  removeEmployee: async (shopId: string, employeeUserId: string): Promise<void> => {
    const shopKind = await resolveShopKindById(shopId)
    if (shopKind === 'brandshop') {
      await apiClient.delete(`/api/v1/brandshops/${shopId}/members/${employeeUserId}`)
      return
    }
    await apiClient.delete(`/api/v1/shops/${shopId}/employees/${employeeUserId}`)
  },
}

